import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email!");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email: email.trim() });
      toast.success("You're on the list! Keep an eye on your inbox 🎨");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again!";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative py-20 sm:py-28 scroll-mt-20"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-[#FFD166] border-2 border-[#073B4C] rounded-[2.5rem] shadow-pop-lg px-6 py-12 sm:px-14 sm:py-16 text-center overflow-hidden"
        >
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#EF476F]/30 blob" />
          <div className="absolute -bottom-12 -right-8 w-48 h-48 bg-[#06D6A0]/40 blob" />

          <div className="relative">
            <span className="inline-flex items-center gap-2 font-heading font-semibold text-sm bg-white border-2 border-[#073B4C] rounded-full px-4 py-1.5 shadow-pop">
              <Mail size={16} strokeWidth={2.5} className="text-[#118AB2]" />
              Join the color club
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#073B4C]">
              Get new books & free pages
            </h2>
            <p className="font-body text-lg text-[#073B4C]/75 mt-4 max-w-xl mx-auto">
              Be the first to know when Wendy releases a new book, plus free
              printable coloring pages delivered to your inbox.
            </p>

            <form
              onSubmit={submit}
              data-testid="newsletter-form"
              className="flex flex-col sm:flex-row items-stretch gap-3 max-w-xl mx-auto mt-8"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input"
                className="flex-1 font-body font-semibold bg-white text-[#073B4C] border-2 border-[#073B4C] rounded-full px-6 py-3.5 shadow-pop placeholder:text-[#073B4C]/40 focus:outline-none focus:-translate-y-0.5 transition-transform"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-btn"
                className="inline-flex items-center justify-center gap-2 font-heading font-semibold bg-[#EF476F] text-white border-2 border-[#073B4C] rounded-full px-7 py-3.5 shadow-pop hover:-translate-y-1 hover:rotate-2 transition-transform disabled:opacity-70"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  "Sign me up!"
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
